import { Locale } from '@finxone-platform/shared/enums';
import { AlertTypes } from '@finxone-platform/shared/sys-config-types';
import { AccountErrorCodes } from '../error-codes/account.type';
import { AccountSuccessCodes } from '../success-codes/account.type';
import { SupportedToasterLocales } from './general.utils';

const defaultAccountTranslations = {
  // ERROR
  ACCOUNT_NICKNAME_UPDATE_FAILED_TITLE: 'Update Failed',
  ACCOUNT_NICKNAME_UPDATE_FAILED_DESCRIPTION: "We couldn't update your nickname. Please try again.",
  ACCOUNT_NICKNAME_MAX_LENGTH_TITLE: 'Nickname too long',
  ACCOUNT_NICKNAME_MAX_LENGTH_DESCRIPTION: 'Account name cannot exceed {{maxLength}} characters.',
  ACCOUNT_ALREADY_CLOSED_TITLE: 'This account is already closed',
  ACCOUNT_ALREADY_CLOSED_DESCRIPTION: 'No further action is required.',
  ACCOUNT_ZERO_BALANCE_TITLE: 'Account must have a zero balance to be closed',
  ACCOUNT_ZERO_BALANCE_DESCRIPTION: 'Transfer remaining funds before trying again.',
  ACCOUNT_CLOSED_ERROR_TITLE: 'Something went wrong while closing the account.',
  ACCOUNT_CLOSED_ERROR_DESCRIPTION: 'Please try again or contact support if issue persists.',
  ACCOUNT_CLOSE_COMPLIANCE_ERROR_TITLE: 'This account cannot be closed due to compliance restrictions.',
  ACCOUNT_CLOSE_COMPLIANCE_ERROR_DESCRIPTION:
    'Please review regulatory obligations or contact compliance team.',
  AMOUNT_NOT_VALID_TITLE: 'Amount not valid',
  AMOUNT_NOT_VALID_DESCRIPTION: 'Account amount is not valid.',
  MAX_DEPOSIT_LIMIT_TITLE: 'Max deposit limit',
  MAX_DEPOSIT_LIMIT_DESCRIPTION: 'You have reached maximum deposit limit.',
  STATEMENT_INVALID_DATE_FUTURE_TITLE: 'Invalid statement date',
  STATEMENT_INVALID_DATE_FUTURE_DESCRIPTION: 'Statement date cannot be in the future.',

  // SUCCESS
  ACCOUNT_NICKNAME_UPDATED_TITLE: 'Nickname Updated',
  ACCOUNT_NICKNAME_UPDATED_DESCRIPTION: 'Your account nickname was successfully changed',
  ACCOUNT_CLOSED_TITLE: 'Account successfully closed',
  ACCOUNT_CLOSED_DESCRIPTION: 'This ledger account has been moved to closed records.',
  ACCOUNT_OPEN_REQUEST_SUCCESS_TITLE: 'Account request opened successfully',
  ACCOUNT_OPEN_REQUEST_SUCCESS_DESCRIPTION: 'Your new account is now awaiting approval.',
  ACCOUNT_OPEN_SUCCESS_TITLE: 'Account opened successfully',
  ACCOUNT_OPEN_SUCCESS_DESCRIPTION: 'Your new account is now approved.',
  ACCOUNT_APPLICANT_CHECK_FAILED_TITLE: 'Account applicant check failed',
  ACCOUNT_APPLICANT_CHECK_FAILED_DESCRIPTION:
    'Applicant check failure. Please try again or contact support if issue persists.',
  MATURITY_DECISION_SUCCESS_TITLE: 'Maturity decision successful',
  MATURITY_DECISION_SUCCESS_DESCRIPTION: 'Your account maturity decision was successfully approved.',
  MATURITY_DECISION_FAILED_TITLE: 'Maturity decision failed',
  MATURITY_DECISION_FAILED_DESCRIPTION: 'Your account maturity decision was failed.',
  DEPOSIT_VALIDATION_FAILED_TITLE: 'Payment validation failed',
  DEPOSIT_VALIDATION_FAILED_DESCRIPTION:
    'The payment request could not be processed. Please check your details and try again.',
  INSTITUTION_ACTION_NOT_SUPPORTED_TITLE: 'Bank not supported for this payment type',
  INSTITUTION_ACTION_NOT_SUPPORTED_DESCRIPTION:
    'Your selected bank does not support this type of payment. Please choose a different bank to continue.',
  NOMINATED_BANK_ACCOUNT_UPDATED_TITLE: 'Nominated account updated',
  NOMINATED_BANK_ACCOUNT_UPDATED_DESCRIPTION: 'Your nominated bank account has been updated successfully.',
  NOMINATED_BANK_ACCOUNT_UPDATE_FAILED_TITLE: 'Update failed',
  NOMINATED_BANK_ACCOUNT_UPDATE_FAILED_DESCRIPTION:
    'We could not update your nominated bank account. Please try again.',
  COP_VERIFICATION_EXPIRED_TITLE: 'CoP Check Expired',
  COP_VERIFICATION_EXPIRED_DESCRIPTION:
    'Your account verification has expired. Please re-confirm your details to continue.',
  NOMINATED_ACCOUNT_MISMATCH_TITLE: 'Account does not match',
  NOMINATED_ACCOUNT_MISMATCH_DESCRIPTION:
    'The bank account you connected does not match the nominated account you provided during your application. Please connect that account instead.',
  FIXED_TERM_ACCOUNT_FUNDED_TITLE: 'Account is fixed until maturity',
  FIXED_TERM_ACCOUNT_FUNDED_DESCRIPTION:
    'This account is fixed until its maturity date and cannot be closed early. You can choose what happens to it from your maturity options.',
  NOMINATED_ACCOUNT_REQUIRED_TITLE: 'Nominated account needed',
  NOMINATED_ACCOUNT_REQUIRED_DESCRIPTION:
    'You need a nominated bank account before this account can be closed. Please add one and try again.',
  ACCOUNT_PAYEE_NOT_FOUND_TITLE: 'Nominated account not set up',
  ACCOUNT_PAYEE_NOT_FOUND_DESCRIPTION:
    'Your nominated bank account has not been set up on this account yet, so funds cannot be sent to it. Please try again shortly or contact support.',
};

export const accountTranslations: {
  // For new locales just add them here to the extract
  [key in SupportedToasterLocales]: typeof defaultAccountTranslations;
} = {
  [Locale.en_GB]: defaultAccountTranslations,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateAccountAlerts = (translations: typeof defaultAccountTranslations) => ({
  // ERROR
  [AccountErrorCodes.ACCOUNT_NICKNAME_UPDATE_FAILED]: {
    name: 'Account nickname update failed',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_NICKNAME_UPDATE_FAILED_TITLE,
    description: translations.ACCOUNT_NICKNAME_UPDATE_FAILED_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_ALREADY_CLOSED]: {
    name: 'Account already closed',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_ALREADY_CLOSED_TITLE,
    description: translations.ACCOUNT_ALREADY_CLOSED_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_ZERO_BALANCE]: {
    name: 'Account zero balance',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_ZERO_BALANCE_TITLE,
    description: translations.ACCOUNT_ZERO_BALANCE_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_CLOSED_ERROR]: {
    name: 'Account closed error',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_CLOSED_ERROR_TITLE,
    description: translations.ACCOUNT_CLOSED_ERROR_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_CLOSE_COMPLIANCE_ERROR]: {
    name: 'Account close compliance error',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_CLOSE_COMPLIANCE_ERROR_TITLE,
    description: translations.ACCOUNT_CLOSE_COMPLIANCE_ERROR_DESCRIPTION,
  },
  // SUCCESS
  [AccountSuccessCodes.ACCOUNT_NICKNAME_UPDATED]: {
    name: 'Account nickname updated',
    type: AlertTypes.SUCCESS,
    title: translations.ACCOUNT_NICKNAME_UPDATED_TITLE,
    description: translations.ACCOUNT_NICKNAME_UPDATED_DESCRIPTION,
  },
  [AccountSuccessCodes.ACCOUNT_CLOSED]: {
    name: 'Account successfully closed',
    type: AlertTypes.SUCCESS,
    title: translations.ACCOUNT_CLOSED_TITLE,
    description: translations.ACCOUNT_CLOSED_DESCRIPTION,
  },
  [AccountSuccessCodes.ACCOUNT_OPEN_SUCCESS]: {
    name: 'Account opened successfully',
    type: AlertTypes.SUCCESS,
    title: translations.ACCOUNT_OPEN_SUCCESS_TITLE,
    description: translations.ACCOUNT_OPEN_SUCCESS_DESCRIPTION,
  },
  [AccountSuccessCodes.ACCOUNT_OPEN_REQUEST_SUCCESS]: {
    name: 'Account open request submitted successfully',
    type: AlertTypes.SUCCESS,
    title: translations.ACCOUNT_OPEN_REQUEST_SUCCESS_TITLE,
    description: translations.ACCOUNT_OPEN_REQUEST_SUCCESS_DESCRIPTION,
  },
  [AccountErrorCodes.AMOUNT_NOT_VALID]: {
    name: 'Account amount not valid',
    type: AlertTypes.ERROR,
    title: translations.AMOUNT_NOT_VALID_TITLE,
    description: translations.AMOUNT_NOT_VALID_DESCRIPTION,
  },
  [AccountErrorCodes.MAX_DEPOSIT_LIMIT]: {
    name: 'Account max deposit limit',
    type: AlertTypes.ERROR,
    title: translations.MAX_DEPOSIT_LIMIT_TITLE,
    description: translations.MAX_DEPOSIT_LIMIT_DESCRIPTION,
  },
  [AccountErrorCodes.APPLICANT_CHECK_FAILED]: {
    name: 'Applicant check failed',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_APPLICANT_CHECK_FAILED_TITLE,
    description: translations.ACCOUNT_APPLICANT_CHECK_FAILED_DESCRIPTION,
  },
  [AccountErrorCodes.MATURITY_DECISION_FAILED]: {
    name: 'Maturity decision failed',
    type: AlertTypes.ERROR,
    title: translations.MATURITY_DECISION_FAILED_TITLE,
    description: translations.MATURITY_DECISION_FAILED_DESCRIPTION,
  },
  [AccountSuccessCodes.MATURITY_DECISION_SUCCESS]: {
    name: 'Maturity decision approved',
    type: AlertTypes.SUCCESS,
    title: translations.MATURITY_DECISION_SUCCESS_TITLE,
    description: translations.MATURITY_DECISION_SUCCESS_DESCRIPTION,
  },
  [AccountErrorCodes.STATEMENT_INVALID_DATE_FUTURE]: {
    name: 'Invalid statement date',
    type: AlertTypes.ERROR,
    title: translations.STATEMENT_INVALID_DATE_FUTURE_TITLE,
    description: translations.STATEMENT_INVALID_DATE_FUTURE_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_NICKNAME_MAX_LENGTH]: {
    name: 'Account nickname too long',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_NICKNAME_MAX_LENGTH_TITLE,
    description: translations.ACCOUNT_NICKNAME_MAX_LENGTH_DESCRIPTION,
  },
  [AccountErrorCodes.DEPOSIT_VALIDATION_FAILED]: {
    name: 'Deposit validation failed',
    type: AlertTypes.ERROR,
    title: translations.DEPOSIT_VALIDATION_FAILED_TITLE,
    description: translations.DEPOSIT_VALIDATION_FAILED_DESCRIPTION,
  },
  [AccountErrorCodes.INSTITUTION_ACTION_NOT_SUPPORTED]: {
    name: 'Institution does not support action',
    type: AlertTypes.ERROR,
    title: translations.INSTITUTION_ACTION_NOT_SUPPORTED_TITLE,
    description: translations.INSTITUTION_ACTION_NOT_SUPPORTED_DESCRIPTION,
  },
  [AccountSuccessCodes.NOMINATED_BANK_ACCOUNT_UPDATED]: {
    name: 'Nominated bank account updated',
    type: AlertTypes.SUCCESS,
    title: translations.NOMINATED_BANK_ACCOUNT_UPDATED_TITLE,
    description: translations.NOMINATED_BANK_ACCOUNT_UPDATED_DESCRIPTION,
  },
  [AccountErrorCodes.NOMINATED_BANK_ACCOUNT_UPDATE_FAILED]: {
    name: 'Nominated bank account update failed',
    type: AlertTypes.ERROR,
    title: translations.NOMINATED_BANK_ACCOUNT_UPDATE_FAILED_TITLE,
    description: translations.NOMINATED_BANK_ACCOUNT_UPDATE_FAILED_DESCRIPTION,
  },
  [AccountErrorCodes.COP_CHECK_VERIFICATION_EXPIRED]: {
    name: 'Cop expired',
    type: AlertTypes.ERROR,
    title: translations.COP_VERIFICATION_EXPIRED_TITLE,
    description: translations.COP_VERIFICATION_EXPIRED_DESCRIPTION,
  },
  [AccountErrorCodes.NOMINATED_ACCOUNT_MISMATCH]: {
    name: 'Nominated account mismatch',
    type: AlertTypes.ERROR,
    title: translations.NOMINATED_ACCOUNT_MISMATCH_TITLE,
    description: translations.NOMINATED_ACCOUNT_MISMATCH_DESCRIPTION,
  },
  [AccountErrorCodes.FIXED_TERM_ACCOUNT_FUNDED]: {
    name: 'Fixed term account funded',
    type: AlertTypes.ERROR,
    title: translations.FIXED_TERM_ACCOUNT_FUNDED_TITLE,
    description: translations.FIXED_TERM_ACCOUNT_FUNDED_DESCRIPTION,
  },
  [AccountErrorCodes.NOMINATED_ACCOUNT_REQUIRED]: {
    name: 'Nominated account required',
    type: AlertTypes.ERROR,
    title: translations.NOMINATED_ACCOUNT_REQUIRED_TITLE,
    description: translations.NOMINATED_ACCOUNT_REQUIRED_DESCRIPTION,
  },
  [AccountErrorCodes.ACCOUNT_PAYEE_NOT_FOUND]: {
    name: 'Account payee not found',
    type: AlertTypes.ERROR,
    title: translations.ACCOUNT_PAYEE_NOT_FOUND_TITLE,
    description: translations.ACCOUNT_PAYEE_NOT_FOUND_DESCRIPTION,
  },
});
